import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { map, startWith } from 'rxjs';

import { FormContact, FormContactCode } from '~/app/domains/help/types/form-contact.type';
import { Option } from '~/app/shared/types/option.type';

const SUBJECT_ORDER: FormContactCode[] = [
    'CONTRACT_REF',
    'UC_CORRECTION',
    'FUND_REF',
    'FEEDBACK',
    'OTHER',
];
import { AlertComponent } from '~/app/shared/components/elements/alert/alert.component';
import { ButtonComponent } from '~/app/shared/components/elements/buttons/button/button.component';
import { H2Component } from '~/app/shared/components/elements/h2/h2.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { FileDropzoneComponent } from '~/app/shared/components/forms/formElements/file-dropzone/file-dropzone.component';
import { SelectTextInputComponent } from '~/app/shared/components/forms/formElements/select-text-input/select-text-input.component';

export type HelpContactFormSubmitData = {
    formUuid: string;
    subject: string;
    message: string;
    files?: File[];
};

@Component({
    selector: 'eb-help-contact-form',
    templateUrl: './help-contact-form.component.html',
    styleUrl: './help-contact-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslocoModule,
        AlertComponent,
        ButtonComponent,
        H2Component,
        LabelComponent,
        FileDropzoneComponent,
        SelectTextInputComponent,
    ],
})
export class HelpContactFormComponent {
    private fb = inject(FormBuilder);

    formContacts = input.required<FormContact[]>();

    submitForm = output<HelpContactFormSubmitData>();
    back = output<void>();

    files = signal<File[]>([]);

    form = this.fb.group({
        subject: ['', Validators.required],
        message: ['', Validators.required],
    });

    private selectedSubject = toSignal(
        this.form.controls.subject.valueChanges.pipe(
            startWith(this.form.controls.subject.value),
            map(value => {
                // eb-select-text-input passes the full Option object, not just the id
                if (value && typeof value === 'object' && 'id' in value) {
                    return (value as Option<string>).id;
                }
                return value || null;
            })
        )
    );

    subjectOptions = computed<Option<string>[]>(() =>
        [...this.formContacts()]
            .sort((a, b) => SUBJECT_ORDER.indexOf(a.code) - SUBJECT_ORDER.indexOf(b.code))
            .map(fc => ({
                id: fc.code,
                label: fc.title,
            }))
    );

    selectedFormContact = computed<FormContact | null>(() => {
        const selectedCode = this.selectedSubject();
        if (!selectedCode) {
            return null;
        }
        return this.formContacts().find(fc => fc.code === selectedCode) ?? null;
    });

    selectedSubjectDescription = computed<string | null>(() =>
        this.selectedFormContact()?.description ?? null
    );

    isAttachmentRequired = computed<boolean>(() =>
        this.selectedFormContact()?.attachmentRequired ?? false
    );

    onSubmit(): void {
        if (this.form.invalid) {
            return;
        }

        if (this.isAttachmentRequired() && this.files().length === 0) {
            return;
        }

        const contact = this.selectedFormContact();
        if (!contact) {
            return;
        }

        const files = this.files();

        this.submitForm.emit({
            formUuid: contact.uuid,
            subject: contact.title,
            message: this.form.getRawValue().message!,
            files: files.length > 0 ? files : undefined,
        });
    }
}
